import {useEffect, useState} from "react";
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Modal,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import {useAlert} from "../context/AlertContext";
import {updatePracticeTask} from "../api/api";
import {UpdatePracticeTaskRequest} from "../types";
import AppModalBox from "../components/AppModalBox";

type EditablePracticeTask = {
    id: number;
    title: string;
    content: string;
    orderNum: number;
};

interface Props {
    open: boolean;
    onClose: () => void;
    task: EditablePracticeTask | null;
    onUpdated: () => void;
}

type EditorMode = "RICH" | "CODE";

const EditPracticeTaskModal = ({open, onClose, task, onUpdated}: Props) => {
    const {showAlert} = useAlert();

    const [title, setTitle] = useState("");
    const [orderNum, setOrderNum] = useState<number>(1);
    const [content, setContent] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState<EditorMode>("RICH");

    useEffect(() => {
        if (!open || !task) return;

        setTitle(task.title ?? "");
        setOrderNum(task.orderNum ?? 1);
        setContent(task.content ?? "");
        setSaving(false);
        setMode("RICH");
    }, [open, task]);

    const ckeditorSx = (theme: any) => ({
        mb: 2,

        "& .ck.ck-editor": {
            width: "100%",
        },

        "& .ck.ck-editor__main > .ck-editor__editable": {
            minHeight: "300px",
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
            borderColor: `${theme.palette.divider} !important`,
        },

        "& .ck-content": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.paper : "#f5f5f5"
            } !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar .ck-button, & .ck.ck-toolbar .ck-button .ck-icon": {
            color: `${theme.palette.text.primary} !important`,
            fill: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-dropdown__panel": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    });

    const codeTextAreaSx = {
        "& .MuiInputBase-input": {
            fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            whiteSpace: "pre",
            lineHeight: 1.5,
        },
    };

    const submit = async () => {
        if (!task) return;

        const req: UpdatePracticeTaskRequest = {
            title: title.trim(),
            content,
            orderNum: Number(orderNum),
        };

        if (!req.title) {
            showAlert("Title is required", "error");
            return;
        }

        if (!req.orderNum || req.orderNum < 1) {
            showAlert("Order must be >= 1", "error");
            return;
        }

        if (!req.content || !req.content.trim()) {
            showAlert("Task content is required", "error");
            return;
        }

        try {
            setSaving(true);
            await updatePracticeTask(task.id, req);
            showAlert("Practice task updated successfully", "success");
            onClose();
            onUpdated();
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to update practice task", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <AppModalBox
                sx={{
                    p: 4,
                    width: "60%",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    outline: "none",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                }}
            >
                <Typography variant="h6" sx={{mb: 1}}>
                    Edit Practice Task
                </Typography>

                <Typography variant="body2" sx={{color: "text.secondary", mb: 2}}>
                    Choose how you want to edit content: Rich editor or Pure HTML/CSS code.
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={mode === "CODE"}
                                onChange={(_, checked) => setMode(checked ? "CODE" : "RICH")}
                            />
                        }
                        label={mode === "CODE" ? "Pure Code Mode" : "Rich Editor Mode"}
                    />

                    <Typography variant="caption" sx={{color: "text.secondary"}}>
                        {mode === "CODE" ? "Edits raw HTML string" : "CKEditor formatting tools"}
                    </Typography>
                </Box>

                <Divider sx={{mb: 2}} />

                <TextField
                    label="Practice Task Title"
                    fullWidth
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{mb: 2}}
                />

                <TextField
                    label="Order"
                    type="number"
                    fullWidth
                    required
                    value={orderNum}
                    onChange={(e) => setOrderNum(Number(e.target.value))}
                    sx={{mb: 2}}
                    slotProps={{
                        htmlInput: {
                            min: 1,
                        },
                    }}
                />

                <Typography sx={{mb: 1}}>Task Content</Typography>

                {mode === "RICH" ? (
                    <Box sx={ckeditorSx}>
                        <CKEditor
                            editor={ClassicEditor as any}
                            data={content}
                            onChange={(_event: any, editor: any) => {
                                const html = editor.getData();
                                setContent(html);
                            }}
                            config={{mediaEmbed: {previewsInData: true}}}
                        />
                    </Box>
                ) : (
                    <TextField
                        fullWidth
                        multiline
                        minRows={14}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        sx={{mb: 2, ...codeTextAreaSx}}
                        slotProps={{
                            input: {
                                spellCheck: false,
                            },
                        }}
                    />
                )}

                <Box sx={{display: "flex", gap: 2, mt: 1}}>
                    <Button onClick={submit} variant="contained" disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>

                    <Button onClick={onClose} variant="outlined" disabled={saving}>
                        Cancel
                    </Button>
                </Box>
            </AppModalBox>
        </Modal>
    );
};

export default EditPracticeTaskModal;